import React from "react";
import {
  TextField,
  Typography,
  Box,
  Autocomplete,
  InputAdornment,
  IconButton,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Controller } from "react-hook-form";
import InputMask from "react-input-mask";
import PropTypes from "prop-types";

const BasicInformation = ({
  isDisabled,
  control,
  errors,
  watch,
  setValue,
  allCities,
  allStates,
  allCounties,
  loadingCounties,
  handleGoogleCallback,
  state,
  setState,
  showSSN,
  setShowSSN,
  pii_access,
  role,
  data,
  GoogleAutoComplete,
}) => {
  return (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        Basic Information
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="firstName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="First Name"
                fullWidth
                required
                disabled={isDisabled}
                variant="outlined"
                error={!!errors.firstName}
                helperText={errors.firstName ? "First name is required" : ""}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="middleName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isDisabled}
                label="Middle Name"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="lastName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Last Name"
                fullWidth
                required
                disabled={isDisabled}
                variant="outlined"
                error={!!errors.lastName}
                helperText={errors.lastName ? "Last name is required" : ""}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="preferredName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isDisabled}
                label="Preferred Name"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="dob"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Date of Birth"
                type="date"
                disabled={isDisabled}
                slotProps={{ inputLabel: { shrink: true }}}
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="county"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allCounties}
                getOptionLabel={(option) => option || ""}
                loading={loadingCounties}
                onChange={(event, newValue) => {
                  setValue("county", newValue || "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Home County"
                    fullWidth
                    required
                    disabled={isDisabled}
                    variant="outlined"
                    error={!!errors.county}
                    helperText={errors.county ? "County is required" : ""}
                    slotProps={{ input: { ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCounties ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}}
                  />
                )}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(0, 0, 0, 0.6)",
              marginBottom: "4px",
              fontWeight: 500,
            }}
          >
            Search Address
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <GoogleAutoComplete
              placeholder="Search Address"
              callback={handleGoogleCallback}
            />
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="address"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                disabled={isDisabled}
                {...field}
                label="Street Address"
                fullWidth
                variant="outlined"
                required
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="zipCode"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                disabled={isDisabled}
                {...field}
                label="ZIP/Postal Code"
                fullWidth
                variant="outlined"
                required
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="city"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allCities}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    fullWidth
                    disabled={isDisabled}
                    variant="outlined"
                    error={!!errors.city}
                    helperText={errors.city ? "City is required" : ""}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="state"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                {...field}
                options={allStates}
                getOptionLabel={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    disabled={isDisabled}
                    fullWidth
                    variant="outlined"
                    error={!!errors.state}
                    helperText={errors.state ? "State is required" : ""}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                disabled={isDisabled}
                fullWidth
                variant="outlined"
                error={!!errors.email}
                helperText={errors.email ? "Email is required" : ""}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography>Will you prefer to e-sign the Indemnity Agreement?</Typography>
          <Controller
            name="is_esignatures"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel
                  disabled={isDisabled}
                  value="Yes"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  disabled={isDisabled}
                  value="No"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography>May we text you?</Typography>
          <Controller
            name="mayWeText"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel
                  disabled={isDisabled}
                  value="Yes"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  disabled={isDisabled}
                  value="No"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 9 }}>
          <Controller
            name="cell_phone"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <InputMask
                mask="999-999-9999"
                value={field.value}
                onChange={field.onChange}
                disabled={isDisabled}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="Cell Phone"
                    disabled={isDisabled}
                    fullWidth
                    variant="outlined"
                    placeholder="123-456-7890"
                  />
                )}
              </InputMask>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Typography>Are you a U.S. Citizen?</Typography>
          <Controller
            name="usCitizen"
            control={control}
            defaultValue="Yes"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel
                  disabled={isDisabled}
                  value="Yes"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  disabled={isDisabled}
                  value="No"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Controller
            name="ssn"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label="Social Security Number"
                disabled={isDisabled}
                type={pii_access && showSSN ? "text" : "password"}
                fullWidth
                variant="outlined"
                error={!!errors.ssn}
                helperText={errors.ssn ? "SSN is required" : ""}
                slotProps={{ input: {
                  endAdornment:
                    pii_access ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle ssn visibility"
                          onClick={() => setShowSSN((prev) => !prev)}
                          edge="end"
                        >
                          {showSSN ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}}
              />
            )}
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              border: "1px solid #d1d5db", // Light gray border
              borderRadius: "8px", // Rounded corners
              backgroundColor: "#f9fafb", // Light background
              padding: "16px", // Spacing inside the box
              margin: "16px 0", // Vertical spacing outside the box
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: "2px" }}>
              Authorization to Obtain Underwriting Information
            </Typography>
            <Typography>
              Do you fully understand and agree, by submitting this request
              for approval you are providing authorization for Probate Bond
              Pros, surety companies and/or third-party brokers to verify
              information you provided as well as permission to obtain your
              consumer information from any source which would be necessary to
              determine bonding eligibility? This includes obtaining credit
              reports at time of application, during any review or renewal, at
              the time of any potential or actual claim, and/or for any other
              legitimate purposes determined by the surety in its reasonable
              discretion. This information will be held in the strictest
              confidence. Do you agree?
            </Typography>

            <Controller
              name="authorizationAgreement"
              control={control}
              defaultValue={true} // Default value set to true (Yes)
              rules={{ required: "You must select an option" }}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                    disabled={isDisabled}
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                    disabled={isDisabled}
                  />
                </RadioGroup>
              )}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

BasicInformation.propTypes = {
  isDisabled: PropTypes.bool,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  allCities: PropTypes.array,
  allStates: PropTypes.array,
  allCounties: PropTypes.array,
  loadingCounties: PropTypes.bool,
  handleGoogleCallback: PropTypes.func.isRequired,
  state: PropTypes.object,
  setState: PropTypes.func.isRequired,
  showSSN: PropTypes.bool,
  setShowSSN: PropTypes.func.isRequired,
  pii_access: PropTypes.bool,
  role: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
};

export default BasicInformation;
